import { useHubble } from '@seek/hubble';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Button,
  Box,
  Stack,
  Text,
  IconArrow,
  Loader,
} from 'braid-design-system';
import { useRef, useState, useEffect } from 'react';
import Cropper, { type ReactCropperElement } from 'react-cropper';

import { useAppConfig } from 'src/modules/appConfig';

import { AddPhotoInput } from '../AddPhotoInput/AddPhotoInput';
import { resizeImage } from '../resizeImage';
import { SystemError, ValidationError } from '../validateImage';

import translations from './.vocab';

import * as styles from '../../AvatarContainer/AvatarContainer.css';

export const calculateMinCropBoxDisplayPx = ({
  cropBoxWidth,
  cropBoxHeight,
  cropDataWidth,
  cropDataHeight,
  minCropOutputPx,
}: {
  cropBoxWidth: number;
  cropBoxHeight: number;
  cropDataWidth?: number;
  cropDataHeight?: number;
  minCropOutputPx: number;
}): number | undefined => {
  /**
   * cropBox comes from Cropper's getCropBoxData():
   * - The visible crop box size measured in UI/CSS pixels (what the user drags).
   *
   * cropData comes from Cropper's getData():
   * - The crop size measured in *source image pixels*.
   *
   * We use these together to convert "minimum 256x256 source pixels" into
   * "minimum N UI pixels" for the current scale.
   *
   * Example:
   * - cropDataWidth = 512 (source px)
   * - cropBoxWidth = 256 (UI px)
   * => pxPerUiX = 2 source px per UI px
   * => min UI px needed for 256 source px is 256 / 2 = 128
   */

  // How many *source image pixels* each UI pixel represents.
  const pxPerUiX = cropBoxWidth > 0 ? (cropDataWidth ?? 0) / cropBoxWidth : 0;
  const pxPerUiY =
    cropBoxHeight > 0 ? (cropDataHeight ?? 0) / cropBoxHeight : 0;

  if (pxPerUiX > 0 && pxPerUiY > 0) {
    // We want at least minCropOutputPx source pixels in both directions.
    // Convert that requirement into UI pixels using the current scale.
    return Math.ceil(
      Math.max(minCropOutputPx / pxPerUiX, minCropOutputPx / pxPerUiY),
    );
  }

  return undefined;
};

interface Props {
  originalFile: File | null;
  setOriginalFile: (file: File | null) => void;
  setCroppedImage: (file: File | null) => void;
  navigateToUploadPreviewStep: () => void;
  navigateToCropStep: () => void;
  openDialog: () => void;
  setValidationError: (value: ValidationError | SystemError | false) => void;
}

export const CropAvatar = ({
  originalFile,
  setOriginalFile,
  setCroppedImage,
  navigateToUploadPreviewStep,
  navigateToCropStep,
  openDialog,
  setValidationError,
}: Props) => {
  const MIN_CROP_OUTPUT_PX = 1;
  const [isCroppingImage, setIsCroppingImage] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');
  const [minCropBoxPx, setMinCropBoxPx] = useState<number | undefined>(
    undefined,
  );
  const cropperRef = useRef<ReactCropperElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasTrackedDisplayForCurrentFileRef = useRef(false);
  const { t } = useTranslations(translations);
  const hubble = useHubble();
  const { zone, language } = useAppConfig();

  useEffect(() => {
    hubble.trackEvent('component_viewed', {
      currentPageSection: 'photo crop',
      component: {
        type: 'card',
        name: 'photo crop',
      },
      displayLanguage: language,
      zone,
    });
  }, [hubble, language, zone]);

  useEffect(() => {
    setIsCroppingImage(false);
    setMinCropBoxPx(undefined);
    if (!originalFile) {
      setImagePreviewUrl('');
      return;
    }

    hasTrackedDisplayForCurrentFileRef.current = false;
    // Use an object URL so the image can render immediately (avoids FileReader/base64 delay).
    const objectUrl = URL.createObjectURL(originalFile);
    setImagePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [originalFile]);

  const onCropperReady = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) {
      return;
    }

    if (!hasTrackedDisplayForCurrentFileRef.current) {
      hasTrackedDisplayForCurrentFileRef.current = true;
      metrics.count('avatar_photo_displayed');
    }

    const cropData = cropper.getData();
    const cropBox = cropper.getCropBoxData();

    const nextMin = calculateMinCropBoxDisplayPx({
      cropBoxWidth: cropBox.width,
      cropBoxHeight: cropBox.height,
      cropDataWidth: cropData.width,
      cropDataHeight: cropData.height,
      minCropOutputPx: MIN_CROP_OUTPUT_PX,
    });

    // Store it so Cropper can enforce it via minCropBoxWidth/Height.
    if (typeof nextMin === 'number') {
      setMinCropBoxPx((prev) => (prev === nextMin ? prev : nextMin));
    }
  };

  const handleContinue = async () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'photo crop',
      component: {
        type: 'button',
        name: 'continue',
      },
      displayLanguage: language,
      zone,
    });

    const imageElement = cropperRef.current;
    const cropper = imageElement?.cropper;

    if (!cropper) {
      return;
    }

    setIsCroppingImage(true);

    try {
      const croppedCanvas = cropper.getCroppedCanvas();
      const normalisedFile = await resizeImage(croppedCanvas);
      setCroppedImage(normalisedFile);
      navigateToUploadPreviewStep();
    } catch (error: unknown) {
      if (error instanceof ValidationError || error instanceof SystemError) {
        setValidationError(error);
      } else {
        const message =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred';
        setValidationError(
          new SystemError(message, {
            fileType: originalFile?.type,
            fileSize: originalFile
              ? `${(originalFile.size / 1024).toFixed(2)} KB`
              : undefined,
            file: originalFile?.name,
          }),
        );
      }
      setIsCroppingImage(false);
    }
  };

  const hasPreview = Boolean(imagePreviewUrl);

  const handleBrowseClick = () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'photo crop',
      component: {
        type: 'button',
        name: 'select a different photo',
      },
      displayLanguage: language,
      zone,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  return (
    <Stack space="medium">
      <Text tone="secondary" size="small">
        {t('Move and resize your photo to fit')}
      </Text>
      <Box
        width="full"
        overflow="hidden"
        className={styles.disableLongPressMenu}
        style={{
          height: 350,
        }}
        data-testid="avatar-cropper"
      >
        {hasPreview ? (
          <Cropper
            key={`${imagePreviewUrl}-${minCropBoxPx}`}
            ref={cropperRef}
            src={imagePreviewUrl}
            style={{ height: '100%', width: '100%', display: 'block' }}
            viewMode={1}
            responsive={true}
            restore={false}
            checkOrientation={false}
            guides={false}
            dragMode="none"
            rotatable={false}
            zoomable={false}
            autoCropArea={1}
            initialAspectRatio={1}
            aspectRatio={1}
            minCropBoxWidth={minCropBoxPx}
            minCropBoxHeight={minCropBoxPx}
            ready={onCropperReady}
            toggleDragModeOnDblclick={false}
            background={false}
          />
        ) : (
          <Box
            height="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Loader size="small" />
          </Box>
        )}
      </Box>
      <Box
        width="full"
        display="flex"
        flexDirection={{ mobile: 'columnReverse', tablet: 'row' }}
        justifyContent={{ mobile: 'flexStart', tablet: 'spaceBetween' }}
        gap="small"
      >
        <Box
          display="flex"
          justifyContent={{ mobile: 'center', tablet: 'flexStart' }}
        >
          <Button
            tone="formAccent"
            variant="transparent"
            onClick={handleBrowseClick}
          >
            {t('Select a different photo')}
          </Button>
        </Box>
        <Box
          display="flex"
          justifyContent={{ mobile: 'flexStart', tablet: 'flexEnd' }}
        >
          <Button
            tone="formAccent"
            onClick={handleContinue}
            loading={isCroppingImage}
            icon={<IconArrow direction="right" />}
            iconPosition="trailing"
          >
            {t('Continue')}
          </Button>
        </Box>
      </Box>
      <AddPhotoInput
        navigateToCropStep={navigateToCropStep}
        openDialog={openDialog}
        fileInputRef={fileInputRef}
        setOriginalFile={setOriginalFile}
        setValidationError={setValidationError}
      />
    </Stack>
  );
};
